<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import type { UpgradeInstall } from '@/net/api';
import CurrencyIcon from '@/ui/CurrencyIcon.vue';
import { formatCountdown } from './format';

/**
 * The upgrade slot of an item's action row while an install is pending: one
 * button, in place of Upgrade, so every row that already lays out an Upgrade
 * button lays this out too (it is the component's root, so the parent's scoped
 * button styles reach it).
 *
 * Running: the countdown and the price to finish now — pressing it asks to skip.
 * Ready: "Install level N" — pressing it installs, for free.
 *
 * The countdown is aged locally from the server's `secondsRemaining`; it never
 * decides the install is ready (`state/upgrade-installs.ts` re-reads for that).
 * The diamond figure is the server's quote at the last read, so it can only be
 * at or above what the server will actually charge.
 */
const props = defineProps<{
  itemId: string;
  install: UpgradeInstall;
  diamonds: number;
  busy: boolean;
  /** Prefix for `data-action`, so card and workbench stay distinguishable. */
  actionPrefix?: string;
}>();

const emit = defineEmits<{
  install: [id: string];
  skip: [id: string];
}>();

const snapshotAt = ref(Date.now());
const now = ref(Date.now());
let timer: ReturnType<typeof setInterval> | null = null;

watch(
  () => props.install,
  () => {
    snapshotAt.value = Date.now();
    now.value = snapshotAt.value;
  },
);

onMounted(() => {
  timer = setInterval(() => (now.value = Date.now()), 1000);
});
onBeforeUnmount(() => {
  if (timer) clearInterval(timer);
});

const remaining = computed(() =>
  Math.max(0, props.install.secondsRemaining - Math.floor((now.value - snapshotAt.value) / 1000)),
);
/** 0–1 through the install, for the fill behind the label. */
const progress = computed(() =>
  props.install.durationSeconds > 0
    ? Math.min(1, 1 - remaining.value / props.install.durationSeconds)
    : 1,
);
const skipCost = computed(() => props.install.skipDiamondCost ?? 0);
const prefix = computed(() => props.actionPrefix ?? '');
</script>

<template>
  <button
    v-if="install.ready"
    type="button"
    class="install ready"
    :data-action="`${prefix}install-upgrade-${itemId}`"
    :disabled="busy"
    @click="emit('install', itemId)"
  >
    Install level {{ install.toLevel }}
  </button>
  <button
    v-else
    type="button"
    class="install running"
    :style="{ '--install-progress': `${(progress * 100).toFixed(1)}%` }"
    :data-action="`${prefix}skip-upgrade-${itemId}`"
    :disabled="busy || diamonds < skipCost"
    :title="`Installing level ${install.toLevel}`"
    @click="emit('skip', itemId)"
  >
    <span class="when">Lv {{ install.toLevel }} · {{ formatCountdown(remaining) }}</span>
    <span class="skip">Finish · {{ skipCost }} <CurrencyIcon kind="diamonds" /></span>
  </button>
</template>

<style scoped>
.install {
  position: relative;
  overflow: hidden;
}

/* The install's progress, as a fill behind the label. */
.install.running::before {
  content: '';
  position: absolute;
  inset: 0 auto 0 0;
  width: var(--install-progress, 0%);
  background: color-mix(in srgb, var(--accent) 22%, transparent);
  pointer-events: none;
}

.install.running > span {
  position: relative;
}

.install.running .when {
  font-variant-numeric: tabular-nums;
  margin-right: 0.4em;
}

.install.ready {
  border-color: var(--accent);
  background: color-mix(in srgb, var(--accent) 30%, transparent);
}

@media (prefers-reduced-motion: no-preference) {
  .install.ready {
    animation: ready-pulse 1.6s ease-in-out infinite;
  }
}

@keyframes ready-pulse {
  50% {
    box-shadow: 0 0 0 2px color-mix(in srgb, var(--accent) 45%, transparent);
  }
}
</style>
